import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Download, Upload } from "lucide-react";

export default function GenerateCategoryImages() {
  const [isGenerating, setIsGenerating] = useState(false);
  const [results, setResults] = useState<Record<string, any>>({});
  const { toast } = useToast();

  const handleGenerate = async () => {
    setIsGenerating(true);
    setResults({});

    try {
      const { data, error } = await supabase.functions.invoke('generate-category-images');

      if (error) throw error;

      setResults(data.results);
      
      const successCount = Object.values(data.results).filter((r: any) => r.success).length;
      toast({
        title: "Generation Complete",
        description: `Successfully generated ${successCount} category images.`,
      });
    } catch (error: any) {
      console.error('Error generating images:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to generate images",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const downloadImage = (category: string, imageData: string) => {
    const link = document.createElement('a');
    link.href = imageData;
    link.download = `${category.toLowerCase().replace(/\s+/g, '-')}.png`;
    link.click();
  };

  const saveToUploads = async (category: string, imageData: string) => {
    try {
      // Convert base64 to blob
      const base64Data = imageData.split(',')[1];
      const blob = await fetch(`data:image/png;base64,${base64Data}`).then(r => r.blob());
      
      const fileName = `category-${category.toLowerCase().replace(/\s+/g, '-')}.png`;
      const file = new File([blob], fileName, { type: 'image/png' });

      // Upload to Supabase storage
      const { error } = await supabase.storage
        .from('lovable-uploads')
        .upload(fileName, file, { upsert: true });

      if (error) throw error;

      toast({
        title: "Success",
        description: `Saved ${category} image to lovable-uploads`,
      });
    } catch (error: any) {
      console.error('Error saving to uploads:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to save image",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <Card className="p-6">
        <h1 className="text-2xl font-bold mb-4">Generate Category Images</h1>
        <p className="text-muted-foreground mb-6">
          Generate AI-powered hero images for each blog category using the Nano banana model.
          This may take several minutes.
        </p>

        <Button 
          onClick={handleGenerate} 
          disabled={isGenerating}
          size="lg"
        >
          {isGenerating ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Generating Images...
            </>
          ) : (
            'Generate All Category Images'
          )}
        </Button>

        {Object.keys(results).length > 0 && (
          <div className="mt-8">
            <h2 className="text-xl font-semibold mb-4">Results:</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(results).map(([category, result]: [string, any]) => (
                <Card key={category} className="p-4">
                  <h3 className="font-semibold mb-2">{category}</h3>
                  {result.success ? (
                    <div className="space-y-2">
                      <img 
                        src={result.imageData} 
                        alt={category}
                        className="w-full h-48 object-cover rounded-lg"
                      />
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => downloadImage(category, result.imageData)}
                          className="flex-1"
                        >
                          <Download className="mr-2 h-4 w-4" />
                          Download
                        </Button>
                        <Button
                          size="sm"
                          variant="default"
                          onClick={() => saveToUploads(category, result.imageData)}
                          className="flex-1"
                        >
                          <Upload className="mr-2 h-4 w-4" />
                          Save to Uploads
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-destructive">
                      Error: {result.error}
                    </p>
                  )}
                </Card>
              ))}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
